import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { baseStyles } from '../styles/shared.js';
import { MIN_EDITOR_WIDTH, MAX_EDITOR_WIDTH, DEFAULT_EDITOR_WIDTH } from '../utils/constants.js';

// ---------------------------------------------------------------------------
// <sysml-resize-handle> — Draggable split divider
//
// Supports two modes:
//   percentage (default) — emits { percentage: number } for editor/diagram split
//   pixel                — emits { px: number } for sidebar/panel resize
//
// Supports two orientations:
//   vertical (default)   — col-resize cursor, measures clientX
//   horizontal           — row-resize cursor, measures clientY
//
// `direction` controls which side the panel is on:
//   start — panel is on the left/top side of the handle
//   end   — panel is on the right/bottom side of the handle
// ---------------------------------------------------------------------------

@customElement('sysml-resize-handle')
export class SysmlResizeHandle extends LitElement {
  static override styles = [
    baseStyles,
    css`
      :host {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        transition: background var(--r5-duration-fast) var(--r5-ease-default);
        user-select: none;
        z-index: 2;
        background: var(--r5-border);
      }

      /* Vertical orientation (default — column splitter) */
      :host(:not([orientation="horizontal"])) {
        width: 6px;
        cursor: col-resize;
      }
      :host(:not([orientation="horizontal"])) .grip {
        width: 2px;
        height: 24px;
      }

      /* Horizontal orientation (row splitter) */
      :host([orientation="horizontal"]) {
        height: 6px;
        cursor: row-resize;
      }
      :host([orientation="horizontal"]) .grip {
        width: 24px;
        height: 2px;
      }

      :host(:hover),
      :host(.dragging) {
        background: var(--r5-primary);
      }
      .grip {
        border-radius: 1px;
        background: var(--r5-text-muted);
        opacity: 0.5;
      }
      :host(:hover) .grip,
      :host(.dragging) .grip {
        background: var(--r5-surface-bright, #fff);
        opacity: 0.9;
      }
    `,
  ];

  /** Container element to measure against (for percentage mode) */
  @property({ attribute: false })
  container: HTMLElement | null = null;

  /** Resize mode: 'percentage' for editor/diagram, 'pixel' for sidebars */
  @property({ type: String, reflect: true })
  mode: 'percentage' | 'pixel' = 'percentage';

  /** Orientation: 'vertical' for column splits, 'horizontal' for row splits */
  @property({ type: String, reflect: true })
  orientation: 'vertical' | 'horizontal' = 'vertical';

  /** Which side the resizable panel is on relative to this handle */
  @property({ type: String, reflect: true })
  direction: 'start' | 'end' = 'start';

  /** Minimum size in pixels (pixel mode only) */
  @property({ type: Number, attribute: 'min-px' })
  minPx = 0;

  /** Maximum size in pixels (pixel mode only) */
  @property({ type: Number, attribute: 'max-px' })
  maxPx = Infinity;

  /** Default size in pixels for reset (pixel mode only) */
  @property({ type: Number, attribute: 'default-px' })
  defaultPx = 0;

  @state() private _dragging = false;

  override render() {
    return html`<div class="grip"></div>`;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener('mousedown', this._onMouseDown);
    this.addEventListener('dblclick', this._onDoubleClick);
    this.addEventListener('touchstart', this._onTouchStart, { passive: false });
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this._stopDrag();
    this.removeEventListener('mousedown', this._onMouseDown);
    this.removeEventListener('dblclick', this._onDoubleClick);
    this.removeEventListener('touchstart', this._onTouchStart);
  }

  private _onMouseDown = (e: MouseEvent): void => {
    e.preventDefault();
    this._startDrag();
    const onMove = (ev: MouseEvent) =>
      this._onMove(this.orientation === 'horizontal' ? ev.clientY : ev.clientX);
    const onUp = () => {
      this._stopDrag();
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  private _onTouchStart = (e: TouchEvent): void => {
    e.preventDefault();
    this._startDrag();
    const onMove = (ev: TouchEvent) => {
      if (ev.touches.length > 0) {
        const t = ev.touches[0];
        this._onMove(this.orientation === 'horizontal' ? t.clientY : t.clientX);
      }
    };
    const onEnd = () => {
      this._stopDrag();
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
    };
    window.addEventListener('touchmove', onMove);
    window.addEventListener('touchend', onEnd);
  };

  private _startDrag(): void {
    this._dragging = true;
    this.classList.add('dragging');
    document.body.style.cursor = this.orientation === 'horizontal' ? 'row-resize' : 'col-resize';
    document.body.style.userSelect = 'none';
    this.dispatchEvent(
      new CustomEvent('resize-start', { bubbles: true, composed: true }),
    );
  }

  private _stopDrag(): void {
    if (!this._dragging) return;
    this._dragging = false;
    this.classList.remove('dragging');
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    this.dispatchEvent(
      new CustomEvent('resize-end', { bubbles: true, composed: true }),
    );
  }

  private _onMove(clientPos: number): void {
    if (this.mode === 'pixel') {
      this._onMovePixel(clientPos);
    } else {
      this._onMovePercentage(clientPos);
    }
  }

  /** Percentage mode — existing editor/diagram split behavior */
  private _onMovePercentage(clientX: number): void {
    const target = this.container ?? this.parentElement;
    if (!target) return;
    const rect = target.getBoundingClientRect();
    let pct = ((clientX - rect.left) / rect.width) * 100;
    pct = Math.max(MIN_EDITOR_WIDTH, Math.min(MAX_EDITOR_WIDTH, pct));
    this.dispatchEvent(
      new CustomEvent('resize', { detail: { percentage: pct }, bubbles: true, composed: true }),
    );
  }

  /** Pixel mode — sidebar/panel resize */
  private _onMovePixel(clientPos: number): void {
    const target = this.container ?? this.parentElement;
    if (!target) return;
    const rect = target.getBoundingClientRect();

    let px: number;
    if (this.orientation === 'horizontal') {
      // Horizontal: measure from top or bottom of container
      if (this.direction === 'start') {
        px = clientPos - rect.top;
      } else {
        px = rect.bottom - clientPos;
      }
    } else {
      // Vertical: measure from left or right of container
      if (this.direction === 'start') {
        px = clientPos - rect.left;
      } else {
        px = rect.right - clientPos;
      }
    }

    px = Math.max(this.minPx, Math.min(this.maxPx, px));
    this.dispatchEvent(
      new CustomEvent('resize', { detail: { px }, bubbles: true, composed: true }),
    );
  }

  private _onDoubleClick = (): void => {
    if (this.mode === 'pixel') {
      // Pixel mode: double-click collapses the panel
      this.dispatchEvent(
        new CustomEvent('collapse', { bubbles: true, composed: true }),
      );
    } else {
      // Percentage mode: reset to default split
      this.dispatchEvent(
        new CustomEvent('resize', {
          detail: { percentage: DEFAULT_EDITOR_WIDTH },
          bubbles: true,
          composed: true,
        }),
      );
    }
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'sysml-resize-handle': SysmlResizeHandle;
  }
}
